import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { useId } from 'react'
import { cn } from '@/shared/lib'

// Нижний паддинг на 2px больше верхнего: без компенсации текст в контроле
// оптически проседает вниз (пустая зона десцендеров ниже базовой линии).
const control =
  'w-full bg-bg-sunken border border-border-default rounded-control px-4 pt-3 pb-[14px] text-text-primary placeholder:text-text-tertiary transition-colors focus:outline-none focus:border-border-accent disabled:opacity-50'

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(control, className)} {...rest} />
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  // resize-none: произвольная высота руками ломает раскладку диалогов.
  return <textarea className={cn(control, 'min-h-28 resize-none', className)} {...rest} />
}

interface FieldProps {
  label: string
  hint?: string
  error?: string
  children: (props: {
    id: string
    'aria-invalid'?: boolean
    'aria-describedby'?: string
  }) => ReactNode
}

// Render-prop, чтобы label корректно связался с любым контролом (id + aria).
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId()
  const messageId = `${id}-msg`
  const message = error ?? hint
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="label">
        {label}
      </label>
      {children({
        id,
        ...(error ? { 'aria-invalid': true } : {}),
        ...(message ? { 'aria-describedby': messageId } : {}),
      })}
      {message && (
        <span
          id={messageId}
          className={cn('text-xs', error ? 'text-accent' : 'text-text-tertiary')}
        >
          {message}
        </span>
      )}
    </div>
  )
}
